import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import ModuleEditorBase from './ModuleEditorBase';
import DropZone from '../../../pages/courses/wizard/shared/DropZone';
import { uploadFile, useImageUrl } from '../../../services/uploads';
import { isEmpty, PENDING_PLACEHOLDER } from './contentPlaceholder';

interface VideoEmbedEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: {
      video_url?: string;
      caption?: string;
    };
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: any) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

const MAX_FILE_SIZE_MB = 500;

export default function VideoEmbedEditor({
  module,
  sectionId,
  onUpdate,
  onDelete,
}: VideoEmbedEditorProps) {
  const [details, setDetails] = useState(module.details);
  const [caption, setCaption] = useState(module.details.caption || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const sanitize = useCallback(
    (value: string) => DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }),
    []
  );

  useEffect(() => {
    setDetails(module.details);
    setCaption(module.details.caption || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id, module.details.video_url]);

  // Debounced caption auto-save (mirrors ImageEmbedEditor).
  useEffect(() => {
    const sanitizedCaption = sanitize(caption);
    if (sanitizedCaption !== (details.caption || '')) {
      const timeout = setTimeout(async () => {
        try {
          setIsSaving(true);
          setIsSaved(false);
          const next = { ...details, caption: sanitizedCaption };
          setDetails(next);
          await onUpdate(module.id, { details: next });
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        } catch (error) {
          console.error('Failed to save video caption:', error);
        } finally {
          setIsSaving(false);
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [caption, details, module.id, onUpdate, sanitize]);

  const displayUrl = isEmpty(details.video_url) ? null : details.video_url ?? null;
  const [previewUrl] = useImageUrl(displayUrl);

  const handleFilesSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Only video files are allowed');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadResult = await uploadFile(
        {
          file,
          resource_type: 'module_file' as const,
          resource_id: module.id,
          section_id: sectionId,
        },
        {
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      const nextDetails = { ...details, video_url: uploadResult.key };
      setDetails(nextDetails);
      // Default the caption to the file name (without extension) when empty.
      setCaption((prev) => prev || sanitize(file.name.replace(/\.[^/.]+$/, '')));
      await onUpdate(module.id, { details: nextDetails });
    } catch (error) {
      console.error('Failed to upload video:', error);
      toast.error('Failed to upload video');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 300);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this video?')) return;
    const nextDetails = { ...details, video_url: PENDING_PLACEHOLDER };
    setDetails(nextDetails);
    await onUpdate(module.id, { details: nextDetails });
  };

  return (
    <ModuleEditorBase
      moduleType={module.type}
      moduleTitle={module.title}
      onTitleChange={async (title) => await onUpdate(module.id, { title })}
      onDelete={async () => await onDelete(module.id)}
      isSaving={uploading || isSaving}
      isSaved={isSaved}
    >
      {isEmpty(details.video_url) ? (
        <DropZone
          onFilesSelected={handleFilesSelected}
          accept="video/mp4,video/webm,video/ogg"
          maxSizeMB={MAX_FILE_SIZE_MB}
          variant="media"
        />
      ) : (
        <div className="relative w-full rounded-2xl overflow-hidden group bg-black">
          {previewUrl ? (
            <video
              key={previewUrl}
              controls
              className="w-full max-h-[360px]"
            >
              <source src={previewUrl} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="w-full h-[200px] flex items-center justify-center text-white/70 text-sm">
              Loading preview...
            </div>
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Remove video"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 1l10 10M11 1L1 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {uploading && (
        <div className="mt-2">
          <div className="w-full bg-grey-200 dark:bg-grey-700 rounded-full h-2">
            <div
              className="bg-primary-600 dark:bg-primary-350 h-2 rounded-full transition-[width] duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-text-secondary text-center">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}

      <div className="mt-4">
        <label className="block text-sm font-medium text-text-primary mb-2">
          Caption (optional)
        </label>
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(sanitize(e.target.value))}
          placeholder="e.g. Intro walkthrough"
          maxLength={300}
          className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-surface-primary text-text-primary"
        />
        <p className="mt-1 text-xs text-text-secondary">
          {caption.length}/300 characters
        </p>
      </div>
    </ModuleEditorBase>
  );
}
